import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, PauseCircle, Turtle, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn as cx } from "@/lib/utils";

/**
 * Strip Chinese characters and CJK punctuation so TTS only pronounces the
 * English portion. Removes CJK ideographs, radicals, and full-width
 * punctuation, collapses whitespace, and cleans up bilingual separators like " / ".
 */
export function toEnglishOnly(text) {
  if (!text) return "";
  return text
    // Chinese/Japanese/Korean characters
    .replace(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u30ff]+/g, "")
    // CJK symbols & full-width punctuation
    .replace(/[\u3000-\u303f\uff00-\uffef]+/g, " ")
    // Common bilingual separator
    .replace(/\s*[/·|]\s*/g, " ")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Speaker button that fetches OpenAI TTS for `text` and plays the resulting
 * MP3. Reuses the cached blob URL so tapping twice doesn't re-fetch.
 *
 * Props:
 *   text     — string, the English text to speak (required)
 *   voice    — 'fable' | 'alloy' | 'shimmer' (default 'fable' = British)
 *   speed    — float 0.25-4.0. Default 0.8 = comfortable slow-learner pace.
 *   size     — 'sm' | 'md' (default 'sm')
 *   variant  — 'default' | 'ghost' (default 'default')
 *   label    — optional bilingual { cn, en } for the button label
 *   testId   — data-testid override
 *   showSlowToggle — if true, renders a small 🐢 toggle that halves the speed
 */
export function SpeakButton({
  text,
  voice = "fable",
  speed = 0.8,
  size = "sm",
  variant = "default",
  label,
  testId,
  className,
  showSlowToggle = true,
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [slow, setSlow] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  // Always speak the English portion only — this is a pronunciation-training feature.
  const englishText = useMemo(() => toEnglishOnly(text), [text]);
  const effectiveSpeed = slow ? Math.max(0.5, speed * 0.75) : speed;

  // Invalidate cached blob URL when text, voice, or effective speed changes.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, [englishText, voice, effectiveSpeed]);

  const play = async (e) => {
    e?.stopPropagation();
    if (!englishText) return;
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }
    // Reuse cached URL if we already fetched
    if (urlRef.current) {
      const el = new Audio(urlRef.current);
      audioRef.current = el;
      el.onended = () => setPlaying(false);
      el.onpause = () => setPlaying(false);
      setPlaying(true);
      await el.play().catch(() => setPlaying(false));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(
        "/tts",
        { text: englishText, voice, speed: effectiveSpeed },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const el = new Audio(url);
      audioRef.current = el;
      el.onended = () => setPlaying(false);
      el.onpause = () => setPlaying(false);
      setPlaying(true);
      await el.play().catch(() => setPlaying(false));
    } catch {
      toast.error("Audio unavailable · 无法播放");
    } finally {
      setLoading(false);
    }
  };

  const sizes = {
    sm: "h-8 px-3 text-xs gap-1.5",
    md: "h-10 px-4 text-sm gap-2",
  };
  const variants = {
    default:
      "bg-sky-500 hover:bg-sky-600 text-white shadow-sm",
    ghost:
      "bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-100",
  };

  const Icon = loading ? Loader2 : playing ? PauseCircle : Volume2;

  return (
    <div className={cx("inline-flex items-center gap-1.5", className)}>
      <button
        type="button"
        onClick={play}
        disabled={loading || !englishText}
        data-testid={testId || "speak-btn"}
        data-playing={playing ? "1" : "0"}
        data-speed={effectiveSpeed}
        className={cx(
          "inline-flex items-center rounded-full font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
          sizes[size],
          variants[variant],
        )}
        aria-label={label ? `${label.cn} · ${label.en}` : "Play audio"}
      >
        <Icon
          className={cx(
            size === "md" ? "w-4 h-4" : "w-3.5 h-3.5",
            loading && "animate-spin",
          )}
        />
        {label && (
          <span className="flex items-center gap-1">
            <span className="cn">{label.cn}</span>
            <span className="opacity-90">· {label.en}</span>
          </span>
        )}
      </button>
      {showSlowToggle && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setSlow((v) => !v); }}
          data-testid={`${testId || "speak-btn"}-slower`}
          data-active={slow ? "1" : "0"}
          className={cx(
            "inline-flex items-center gap-1 rounded-full font-bold border transition-colors",
            size === "md" ? "h-10 px-3 text-xs" : "h-8 px-2.5 text-[11px]",
            slow
              ? "bg-amber-500 border-amber-500 text-white shadow-sm"
              : "bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-700",
          )}
          aria-pressed={slow}
          title={slow ? "Extra slow — tap to return to normal slow" : "Play even slower"}
        >
          <Turtle className={size === "md" ? "w-3.5 h-3.5" : "w-3 h-3"} />
          <span className="cn">{slow ? "更慢" : "更慢"}</span>
        </button>
      )}
    </div>
  );
}

export default SpeakButton;
